import React, { useState, memo, useRef, useEffect } from 'react';
import { FiFileText, FiZap, FiLayers, FiLoader, FiDownload, FiDatabase, FiHome, FiTruck, FiBriefcase } from 'react-icons/fi';
import { Button, Badge } from 'reactstrap';
import FileDetailsModal from './FileDetailsModal';
import { getDownloadCuentasFile } from '../../helpers/backend';
import './FileTableRow.css';
// import useProgressStable from '../../hooks/useProgressStable';

// Función para parsear fecha en formato "18/08/2025 11:03 AM"
const parseCustomDate = (dateString) => {
  if (!dateString) return new Date();
  
  // Si ya es un formato ISO válido, usarlo directamente
  if (dateString.includes('T') || dateString.includes('-')) {
    return new Date(dateString);
  }
  
  // Parsear formato "DD/MM/YYYY HH:MM AM/PM"
  const parts = dateString.split(' ');
  if (parts.length >= 2) {
    const datePart = parts[0]; // "18/08/2025"
    const timePart = parts[1]; // "11:03"
    const ampm = parts[2]; // "AM" o "PM"
    
    const [day, month, year] = datePart.split('/');
    const [hours, minutes] = timePart.split(':');
    
    let hour24 = parseInt(hours);
    if (ampm === 'PM' && hour24 !== 12) hour24 += 12;
    if (ampm === 'AM' && hour24 === 12) hour24 = 0;
    
    return new Date(year, month - 1, day, hour24, minutes);
  }
  
  return new Date(dateString);
};

const FileTableRow = memo(({ file, theme, index = 0 }) => {
  // Hook para progreso estable - temporalmente deshabilitado
  // const { progress, step } = useProgressStable(
  //   file.progreso_actual?.percentage || 0,
  //   file.progreso_actual?.current_step || ''
  // );
  const [showFullName, setShowFullName] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Referencia para mantener el último valor conocido de progreso
  const lastKnownProgress = useRef(0);
  const [showModal, setShowModal] = useState(false);

  const getIconForType = (type) => {
    switch(type) {
      case 'CATASTRO_ENEL': return <FiZap className="file-type-icon" />;
      case 'CATASTRO_CIUDAD_LIMPIA':
      case 'CATASTRO_ESTRUCTURA': return <FiLayers className="file-type-icon" />;
      case 'RADICADOS_DIARIOS': return <FiFileText className="file-type-icon" />;
      case 'ESCOMBROS': return <FiTruck className="file-type-icon" />;
      case 'CAU_EXCEL': return <FiDatabase className="file-type-icon" />;
      case 'DATOS_BASICOS': return <FiHome className="file-type-icon" />;
      case 'PROCESOS_COMERCIALES': return <FiBriefcase className="file-type-icon" />;
      default: return <FiFileText className="file-type-icon" />;
    }
  };
  const getDisplayNameForType = (type) => {
    switch(type) {
      case 'CATASTRO_ENEL': return 'Catastro Enel';
      case 'CATASTRO_CIUDAD_LIMPIA': return 'Ciudad Limpia';
      case 'CATASTRO_ESTRUCTURA': return 'Estructura';
      case 'RADICADOS_DIARIOS': return 'Radicados Diarios';
      case 'PLANO_ESCOMBROS': return 'Plano Escombros';
      case 'CAMPOS_CAU': return 'Campos CAU';
      case 'DATOS_BASICOS': return 'Datos Básicos';
      case 'PROCESOS_COMERCIALES': return 'Procesos Comerciales';
      default: return type;
    }
  };

  const getProgressData = (file) => {
    let currentPercentage = 0;
    
    if (file.estado === 'PROCESANDO') {
      currentPercentage = file.progreso_actual?.percentage || lastKnownProgress.current;
    } else if (file.estado === 'COMPLETADO') {
      currentPercentage = 100;
    } else {
      // Para otros estados, mantener el último valor conocido
      currentPercentage = file.progreso_actual?.percentage || lastKnownProgress.current;
    }
    
    // Actualizar la referencia si tenemos un nuevo valor válido
    if (currentPercentage > 0) {
      lastKnownProgress.current = currentPercentage;
    }
    
    return {
      percentage: currentPercentage,
      processed: file.progreso_actual?.processed || 0,
      total: file.progreso_actual?.total || 0
    };
  };

  const progressData = getProgressData(file);
 
  const openDetailsModal = (file) => {
    setSelectedFile(file);
    setShowModal(true);
  };
    const closeModal = () => {
    setShowModal(false);
    setSelectedFile(null);
  };
  
  const handleDownload = async (id) => {
    try {
      const response = await getDownloadCuentasFile(id);
      
      // Crear un blob con los datos de la respuesta
      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] || 'application/octet-stream' 
      });
      
      // Crear un enlace de descarga
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Intentar obtener el nombre del archivo desde los headers
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'archivo_descargado';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Limpiar
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error al descargar el archivo:', error);
      // Aquí podrías mostrar un mensaje de error al usuario
    }
  };

  const getStatusBadge = (estado) => {
    switch(estado) {
      case 'COMPLETADO':
        return <Badge color="success">{estado}</Badge>;
      case 'PROCESANDO':
        return <Badge color="warning">{estado}</Badge>;
      case 'ERROR':
        return <Badge color="danger">{estado}</Badge>;
      default:
        return <Badge color="secondary">{estado}</Badge>;
    }
  };

  // Función para verificar si el archivo puede ser descargado
  const canDownload = (file) => {
    const fileType = file.tipo_archivo || file.tipo || file.type;
    return fileType === 'ESCOMBROS' || fileType === 'RADICADOS_DIARIOS';
  };

  // Función para verificar si el archivo es especial (escombros o radicados)
  const isSpecialFile = (file) => {
    const fileType = file.tipo_archivo || file.tipo || file.type;
    return fileType === 'ESCOMBROS' || fileType === 'RADICADOS_DIARIOS' || fileType === 'PROCESOS_COMERCIALES';
  };

  // Función para obtener el color de fondo según el tipo de archivo (usando los mismos colores del UploadDashboard)
  const getBackgroundColor = (file, theme) => {
    const fileType = file.tipo_archivo || file.tipo || file.type;
    if (fileType === 'ESCOMBROS' || fileType === 'PLANO_ESCOMBROS') {
      // Colores naranjas como en escombros-button
      return theme === 'dark' 
        ? 'rgba(255, 152, 0, 0.12)' 
        : 'linear-gradient(135deg, rgba(255, 152, 0, 0.08), rgba(255, 193, 7, 0.05))';
    } else if (fileType === 'RADICADOS_DIARIOS') {
      // Colores azules como en radicados-button
      return theme === 'dark' 
        ? 'rgba(63, 81, 181, 0.12)' 
        : 'linear-gradient(135deg, rgba(63, 81, 181, 0.08), rgba(92, 107, 192, 0.05))';
    } else if (fileType === 'PROCESOS_COMERCIALES') {
      // Colores morados como en comerciales-button
      return theme === 'dark'
        ? 'rgba(103, 58, 183, 0.12)'
        : 'linear-gradient(135deg, rgba(103, 58, 183, 0.08), rgba(156, 39, 176, 0.05))';
    }
    return 'transparent';
  };

  return (
    <tr 
      className={`table-row-animated ${theme === "dark" ? "dark-row" : ""} ${isSpecialFile(file) ? "special-file-row" : ""}`}
      style={{ 
        background: getBackgroundColor(file, theme),
        color: theme === "dark" ? "#c3cbe4" : "",
        animationDelay: `${index * 0.05}s`
      }}
    >
      <td style={{ color: theme === "dark" ? "#c3cbe4" : "" }}>
        <div className="file-type-container">
          <div className="file-type-icon-wrapper">
            {getIconForType(file.tipo_archivo)}
          </div>
          <span className="file-type-text" title={getDisplayNameForType(file.tipo_archivo)} style={{ color: theme === "dark" ? "#c3cbe4" : "" }}>
            {getDisplayNameForType(file.tipo_archivo)}
          </span>
        </div>
      </td>
      
      <td style={{ color: theme === "dark" ? "#c3cbe4" : "" }}>
        <div className="file-name-container">
          <span 
            className="file-name-text" 
            title={file.archivo_nombre}
            onClick={() => setShowFullName(!showFullName)}
            style={{ color: theme === "dark" ? "#c3cbe4" : "" }}
          >
            {showFullName ? file.archivo_nombre : `${file.archivo_nombre.substring(0, 25)}${file.archivo_nombre.length > 25 ? '...' : ''}`}
          </span>
        </div>
      </td>
      
      <td style={{ color: theme === "dark" ? "#c3cbe4" : "" }}>
        <div className="user-info">
          <span className="user-name">
            {file.usuario?.full_name || 'Sistema'}
          </span>
        </div>
      </td>
      
      <td style={{ color: theme === "dark" ? "#c3cbe4" : "" }}>
        <div className="date-info">
          <span className="date-text">
            {parseCustomDate(file.fecha_creacion).toLocaleDateString('es-ES', {
              day: '2-digit',
              month: '2-digit',
              year: '2-digit'
            })}
          </span>
          <span className="time-text" style={{ color: theme === "dark" ? "#8a92b2" : "#6b7280" }}>
            {parseCustomDate(file.fecha_creacion).toLocaleTimeString('es-ES', {
              hour: '2-digit',
              minute: '2-digit'
            })}
          </span>
        </div>
      </td>
      
      <td>
        <div className="status-container">
          {getStatusBadge(file.estado)}
        </div>
      </td>
      
      <td style={{ color: theme === "dark" ? "#c3cbe4" : "" }}>
        <div className="progress-container-compact">
          <div className="progress-bar-mini">
            <div 
              className="progress-fill-mini"
              style={{ 
                width: `${progressData.percentage}%`,
                minWidth: progressData.percentage > 0 ? '2px' : '0px'
              }}
            ></div>
          </div>
          <span className="progress-text-mini" style={{ color: theme === "dark" ? "#8a92b2" : "#6b7280" }}>
            {progressData.percentage}%
          </span>
        </div>
      </td>
      
      <td>
        <div className="action-buttons-compact">
          <button 
            className={`action-btn info-btn ${theme === 'dark' ? 'dark-btn' : ''}`}
            onClick={() => openDetailsModal(file)}
            title="Ver detalles"
          >
            <FiFileText />
          </button>
          {canDownload(file) && (
            <button 
              className={`action-btn download-btn ${theme === 'dark' ? 'dark-btn' : ''}`}
              onClick={() => handleDownload(file.id)}
              title="Descargar archivo"
            >
              <FiDownload />
            </button>
          )}
        </div>
      </td>

      {/* Modal de detalles */}
      <FileDetailsModal file={selectedFile} isOpen={showModal} onClose={closeModal} />
    </tr>
  );
});

// Función de comparación personalizada para React.memo
FileTableRow.displayName = 'FileTableRow';

const areEqual = (prevProps, nextProps) => {
  // Comparación más específica para el progreso
  const prevProgress = prevProps.file.progreso_actual?.percentage || 0;
  const nextProgress = nextProps.file.progreso_actual?.percentage || 0;
  const prevStep = prevProps.file.progreso_actual?.current_step || '';
  const nextStep = nextProps.file.progreso_actual?.current_step || '';
  
  // Solo re-renderizar si hay cambios significativos (más de 5% de diferencia)
  const progressDiff = Math.abs(nextProgress - prevProgress);
  
  return (
    prevProps.file.id === nextProps.file.id &&
    prevProps.file.estado === nextProps.file.estado &&
    progressDiff < 5 && // Solo re-renderizar si el cambio es mayor a 5%
    prevStep === nextStep &&
    prevProps.theme === nextProps.theme &&
    prevProps.index === nextProps.index
  );
};

export default memo(FileTableRow, areEqual);